import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import * as moment from "moment/moment";
import { ContextService } from "./context.service";
import { Evaluation } from "../model/Evaluation";
import { TokenLogin } from "../model/TokenLogin";
import { EmailLogin } from "../model/EmailLogin";
import { SnackBarService } from "./snack-bar.service";
import { TranslateService } from "@ngx-translate/core";
import { NavigationService } from "./navigation.service";
import { Router } from "@angular/router";
import { RolesService } from "./roles.service";
import { RoleAssignment } from "../model/RoleAssignment";

@Injectable({
  providedIn: "root",
})
export class AuthenticationService {
  authnServiceTokenURL: string = "authn/tokenAuthnService/tokenLogin";
  authnServiceLoginURL: string = "authn/tokenAuthnService/usernameLogin";

  loginWithEmail: boolean = false;

  isAdmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  isSuperadmin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // Can this user assign roles at all — mirrors the backend POST /roles rule
  // (superadmin, or org-admin of at least one organization).
  canAssignRoles: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // The caller's Logto role grants (empty for legacy admins, who get full access).
  currentRoles: BehaviorSubject<RoleAssignment[]> = new BehaviorSubject<RoleAssignment[]>([]);

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private context: ContextService,
    private router: Router,
    private snackBarService: SnackBarService,
    private rolesService: RolesService,
  ) {}

  /**
   * Loads the current user's roles from the backend (live, not from token claims)
   * and updates isSuperadmin. Call after a Logto login is established.
   */
  public refreshCurrentRoles() {
    this.rolesService.me().subscribe({
      next: (me) => {
        this.currentRoles.next(me.assignments);
        this.isSuperadmin.next(me.superadmin);
        this.canAssignRoles.next(
          me.superadmin || me.assignments.some((a) => a.role === "ORG_ADMIN"),
        );
      },
      error: () => {
        this.currentRoles.next([]);
        this.isSuperadmin.next(false);
        this.canAssignRoles.next(false);
      },
    });
  }

  /**
   * Legacy IdP admins are treated as full admins (superadmin-equivalent), mirroring
   * the backend. They have no fine-grained grants, so currentRoles stays empty.
   */
  public applyLegacyFullAdmin() {
    this.currentRoles.next([]);
    this.isSuperadmin.next(true);
    this.canAssignRoles.next(true);
  }

  isOrgAdminOf(organizationId: number): boolean {
    return this.currentRoles.value.some(
      (a) => a.role === "ORG_ADMIN" && a.organizationId === organizationId,
    );
  }

  isFacilityAdminOf(facilityId: number): boolean {
    return this.currentRoles.value.some(
      (a) => a.role === "FACILITY_ADMIN" && a.facilityId === facilityId,
    );
  }

  /** True when the current session was established via Logto (not the legacy IdP). */
  isLogtoSession(): boolean {
    return localStorage.getItem("login_provider") === "logto";
  }

  /** Facility ids this user is a facility-admin of. */
  getFacilityAdminFacilityIds(): number[] {
    return this.currentRoles.value
      .filter((a) => a.role === "FACILITY_ADMIN" && a.facilityId != null)
      .map((a) => a.facilityId as number);
  }

  /** Full admin or org-admin of this org: may add/edit its facilities and edit the org. */
  canManageOrg(organizationId: number): boolean {
    return this.isSuperadmin.value || this.isOrgAdminOf(organizationId);
  }

  /** Only full admins may create organizations. */
  canAddOrganization(): boolean {
    return this.isSuperadmin.value;
  }

  /**
   * May act on evaluations (edit/deactivate/delete/tokens/…). Full admins and
   * org-admins can; a facility-admin-only user cannot (they may only view a
   * finished evaluation's results).
   */
  canManageEvaluations(): boolean {
    return this.isSuperadmin.value || this.currentRoles.value.some((a) => a.role === "ORG_ADMIN");
  }

  /** Full admin, org-admin of the org, or facility-admin of this facility may edit facility data. */
  canEditFacility(organizationId: number, facilityId: number): boolean {
    return this.canManageOrg(organizationId) || this.isFacilityAdminOf(facilityId);
  }

  tokenRequest(token: string, evaluation: Evaluation): Observable<any> {
    let header = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http
      .post(
        this.context._configurationEnvironment.service_engine_base_url +
          this.authnServiceTokenURL,
        new TokenLogin(token, evaluation.id),
        { headers: header },
      )
      .pipe(
        catchError((): Observable<any> => {
          this.snackBarService.error(this.translate.instant("loginFailed"));
          // after handling the error, return a new observable
          // that doesn't emit any values and completes
          throw new Error("Login failed.");
        }),
      );
  }

  loginViaToken(accessCode: string, evaluation: Evaluation) {
    try {
      this.tokenRequest(accessCode, evaluation).subscribe((data) => {
        this.setToken(data.access_token);
        localStorage.setItem("access_token", data.access_token);
        localStorage.setItem(
          "expires_in",
          JSON.stringify(data.expires_in.valueOf()),
        );
        localStorage.setItem(
          "expires_at",
          moment(moment.now()).add(data.expires_in, "seconds").toLocaleString(),
        );
        this.context.activeEvaluation = evaluation;
        if (localStorage.getItem("token")) {
          this.router
            .navigate([
              NavigationService.SURVEYPAGE + "/" + evaluation.id?.toString(),
            ])
            .then();
        }
      });
    } catch (Error) {
      // this.router.navigate()
      this.snackBarService.error(this.translate.instant("loginFailed"));
    }
  }

  loginViaEmail(email: string, password: string): Observable<any> {
    let header = new HttpHeaders({ "Content-Type": "application/json" });
    return this.http
      .post(
        this.context._configurationEnvironment.service_engine_base_url +
          this.authnServiceLoginURL,
        new EmailLogin(email, password),
        { headers: header },
      )
      .pipe(
        catchError((): Observable<any> => {
          this.snackBarService.error(
            this.translate.instant("loginFailedAdmin"),
          );
          // after handling the error, return a new observable
          // that doesn't emit any values and completes
          throw new Error("Login failed.");
        }),
      );
  }

  public logOut() {
    this.isAdmin.next(false);
    this.isSuperadmin.next(false);
    this.canAssignRoles.next(false);
    this.currentRoles.next([]);
    localStorage.removeItem("token");
    localStorage.removeItem("access_token");
    localStorage.removeItem("expires_in");
    localStorage.removeItem("expires_at");
    localStorage.removeItem("login_provider");
  }

  public isLoggedIn() {
    return moment().isBefore(this.getExpiration());
  }

  isLoggedOut() {
    return !this.isLoggedIn();
  }

  getExpiration() {
    const expiration: string = localStorage.getItem("expires_at") || "{}";
    const expiresAt = JSON.parse(expiration);
    return moment(expiresAt);
  }

  public logoutByInactivity() {
    console.log(
      "user-session expired, unpersonalized access_token removed from localStorage.",
    );
    localStorage.removeItem("access_token");
    localStorage.removeItem("expires_at");
    localStorage.removeItem("expires_in");
  }

  // STORE the token in localstore:
  setToken(token: string) {
    localStorage.setItem("token", token);
  }

  // READ the token from localstorage and Deserialize
  getToken(): string | null {
    return (
      localStorage.getItem("token") || sessionStorage.getItem("magicLinkToken")
    );
  }
}
